// ============================================================================
// The one shared view control (item 818): a sort choice, the pin, and the
// phone's Move up / Move down. Every board that lets a person arrange their
// cards — New Accounts, Hiring, Onboarding, the People Desk — renders these
// and nothing of its own.
// ============================================================================
import { useCallback, type CSSProperties, type ReactNode } from 'react';
import {
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  type CollisionDetection,
} from '@dnd-kit/core';
import { ArrowDown, ArrowUp, Pin, PinOff } from 'lucide-react';
import { BOARD_SORT_LABELS, BOARD_SORT_MODES, type BoardSortMode, type BoardViewPrefs } from './board-view';
import type { BoardViewApi } from './use-board-view-prefs';

// ----- the sort choice ------------------------------------------------------

export function BoardViewControls({
  view,
  hasStartDate,
  className = '',
}: {
  view: BoardViewApi;
  /** Offer "Start date soonest" only where the board's cards carry one. */
  hasStartDate: boolean;
  className?: string;
}) {
  if (!view.ready) return null;
  const modes = BOARD_SORT_MODES.filter((m) => hasStartDate || m !== 'start_soonest');
  return (
    <div className={`flex flex-wrap items-center gap-2 text-xs ${className}`}>
      <label className="flex items-center gap-1.5 text-tcs-text-secondary">
        <span>Arrange</span>
        <select
          value={view.prefs.sortMode}
          onChange={(e) => view.setSortMode(e.target.value as BoardSortMode)}
          aria-label="Arrange cards"
          className="rounded-md border border-tcs-border bg-tcs-field px-2 py-1 text-xs text-tcs-text"
        >
          {modes.map((m) => (
            <option key={m} value={m}>
              {BOARD_SORT_LABELS[m]}
            </option>
          ))}
        </select>
      </label>
      <span className="text-tcs-text-muted">
        {view.prefs.sortMode === 'my_order'
          ? 'Drag cards into your order. Pinned cards stay on top.'
          : 'Pinned cards stay on top. Switch to My order to drag.'}
      </span>
      {view.saving && <span className="text-tcs-text-muted">Saving…</span>}
      {view.error && <span className="text-st-danger-fg">{view.error}</span>}
    </div>
  );
}

// ----- collision rule for a board whose cards are also drop targets ---------

/** A card id as a droppable: `card:<id>`. Columns keep their own ids. */
export const CARD_DROP_PREFIX = 'card:';

export function cardDropId(id: string): string {
  return CARD_DROP_PREFIX + id;
}

export function isCardDropId(id: unknown): id is string {
  return typeof id === 'string' && id.startsWith(CARD_DROP_PREFIX);
}

export function cardIdFromDrop(dropId: string): string {
  return dropId.slice(CARD_DROP_PREFIX.length);
}

/**
 * Prefer the card under the pointer, then the column under the pointer, then
 * whatever the rectangles say. Without this a drop lands on the column every
 * time and nothing can ever be reordered.
 */
export const boardCollision: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  const card = within.find((c) => isCardDropId(c.id));
  if (card) return [card];
  const column = within.find((c) => !isCardDropId(c.id));
  if (column) return [column];
  return rectIntersection(args);
};

// ----- one card on an arrangeable board -------------------------------------

export interface BoardCardProps {
  id: string;
  /** The column the card sits in, carried on the drop target for the drag rule. */
  columnId: string;
  /** Cards shown in this column, in their arranged order (for Move up / down). */
  shownIds: readonly string[];
  view: BoardViewApi;
  /** Drag between columns is the board's own right; the view control adds reorder. */
  draggable: boolean;
  /** Render as <li> inside the column's <ul> (the kanbans) or as a <div>. */
  as?: 'li' | 'div';
  children: ReactNode;
}

/**
 * Wraps a card with the pin and, on a phone, Move up / Move down. The wrapper
 * is the drag handle (as the kanbans' own wrappers were) and, in "My order",
 * also a drop target so a card can be dropped onto it.
 */
export function BoardCard({ id, columnId, shownIds, view, draggable, as = 'li', children }: BoardCardProps) {
  const canReorder = draggable && view.prefs.sortMode === 'my_order';
  const drag = useDraggable({ id, disabled: !draggable });
  const drop = useDroppable({ id: cardDropId(id), data: { columnId }, disabled: !canReorder });
  const setRef = useCallback(
    (node: HTMLElement | null) => {
      drag.setNodeRef(node);
      drop.setNodeRef(node);
    },
    [drag.setNodeRef, drop.setNodeRef],
  );
  const style: CSSProperties = drag.transform
    ? {
        transform: `translate3d(${drag.transform.x}px, ${drag.transform.y}px, 0)`,
        opacity: drag.isDragging ? 0.4 : 1,
      }
    : { opacity: drag.isDragging ? 0.4 : 1 };

  const pinned = view.prefs.pinnedIds.includes(id);
  const i = shownIds.indexOf(id);
  const pinnedSet = new Set(view.prefs.pinnedIds);
  const canUp = canReorder && i > 0 && pinnedSet.has(shownIds[i - 1]) === pinned;
  const canDown = canReorder && i >= 0 && i < shownIds.length - 1 && pinnedSet.has(shownIds[i + 1]) === pinned;

  const Tag = as;
  return (
    <Tag
      ref={setRef as never}
      style={style}
      className={`relative ${drop.isOver && canReorder ? 'rounded-lg ring-2 ring-tcs-accent' : ''}`}
      {...(draggable ? drag.listeners : {})}
      {...(draggable ? drag.attributes : {})}
    >
      <div
        className="absolute -right-1.5 -top-1.5 z-10 flex items-center gap-0.5"
        // The tools are not a drag handle and never open the card.
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {view.ready && (
          <button
            type="button"
            onClick={() => view.togglePin(id)}
            aria-label={pinned ? 'Unpin card' : 'Pin card to the top'}
            aria-pressed={pinned}
            title={pinned ? 'Unpin' : 'Pin to the top'}
            className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs ${
              pinned
                ? 'border-tcs-accent bg-tcs-accent text-st-solid-ink'
                : 'border-tcs-border bg-tcs-raised text-tcs-text-muted hover:text-tcs-text'
            }`}
          >
            {pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
          </button>
        )}
        {canReorder && (
          <span className="flex items-center gap-0.5 md:hidden">
            <button
              type="button"
              disabled={!canUp}
              onClick={() => view.move(shownIds, id, 'up')}
              aria-label="Move up"
              className="flex h-6 w-6 items-center justify-center rounded-full border border-tcs-border bg-tcs-raised text-tcs-text-muted shadow-sm disabled:opacity-30"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={!canDown}
              onClick={() => view.move(shownIds, id, 'down')}
              aria-label="Move down"
              className="flex h-6 w-6 items-center justify-center rounded-full border border-tcs-border bg-tcs-raised text-tcs-text-muted shadow-sm disabled:opacity-30"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </div>
      {children}
    </Tag>
  );
}

/** The board's drop rule, shared by every kanban: what a drop means. */
export function resolveBoardDrop(
  overId: unknown,
  overData: { columnId?: string } | undefined,
  activeColumnId: string,
): { kind: 'reorder'; overCardId: string } | { kind: 'move'; columnId: string } | { kind: 'none' } {
  if (isCardDropId(overId)) {
    const col = overData?.columnId;
    if (col === activeColumnId) return { kind: 'reorder', overCardId: cardIdFromDrop(overId) };
    if (col) return { kind: 'move', columnId: col };
    return { kind: 'none' };
  }
  if (typeof overId === 'string') return { kind: 'move', columnId: overId };
  return { kind: 'none' };
}

export type { BoardViewPrefs };
